// js/custom-element/section/product-list/quick-size-selector.js
if (!customElements.get('quick-size-selector')) {
  class QuickSizeSelector extends CustomHTMLElement {
    connectedCallback() {
      this.productHandle = this.getAttribute('product-handle');
      this.productData = null;
      this.isLoading = false;
      this.isOpen = false;

      // Bind event listeners
      this.delegate.on('click', '.product-item__size-button', this._onSizeClick.bind(this));

      // Use direct event listener on toggle button with capture phase to intercept before data-instant
      const toggleButton = this.querySelector('[data-action="toggle-size-selector"]');
      if (toggleButton) {
        toggleButton.addEventListener('click', this._onToggleClick.bind(this), true);
      }

      // Close when clicking outside
      this._onDocumentClick = this._handleOutsideClick.bind(this);

      // Close when mouse leaves the image card
      const imageWrapper = this.closest('.product-item__image-wrapper');
      if (imageWrapper) {
        this._onMouseLeave = this._handleMouseLeave.bind(this);
        imageWrapper.addEventListener('mouseleave', this._onMouseLeave);
      }
    }

    _onToggleClick(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (this.isOpen) {
        this._close();
      } else {
        this._open();
      }
    }

    async _open() {
      // Load product data if not already loaded
      if (!this.productData) {
        await this._loadProductData();
      } else {
        // If data is already loaded, update button availability for current color selection
        this._updateButtonAvailability();
      }

      this.isOpen = true;
      this.classList.add('is-open');

      // Add document click listener to close when clicking outside
      setTimeout(() => {
        document.addEventListener('click', this._onDocumentClick);
      }, 0);
    }

    _close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      document.removeEventListener('click', this._onDocumentClick);
    }

    _handleOutsideClick(event) {
      // Check if click is outside the size selector
      if (!this.contains(event.target)) {
        this._close();
      }
    }

    _handleMouseLeave() {
      // Close the overlay when mouse leaves the image card
      if (this.isOpen) {
        this._close();
      }
    }

    async _loadProductData() {
      try {
        this.productData = await ProductLoader.load(this.productHandle);
        this._updateButtonAvailability();
      } catch (error) {
        console.error('Failed to load product data:', error);
      }
    }

    async _onSizeClick(event, button) {
      if (button.hasAttribute('disabled') || this.isLoading) {
        return;
      }

      // Get size option details
      const sizeValue = button.getAttribute('data-option-value');
      const sizePosition = button.getAttribute('data-option-position');

      // Get selected color from swatch (if exists)
      const productItem = this.closest('product-item');
      const colorSwatch = productItem?.querySelector('.color-swatch__radio:checked');
      const colorValue = colorSwatch?.value;
      const colorSwatchList = colorSwatch?.closest('.product-item-meta__swatch-list');
      const colorPosition = colorSwatchList?.getAttribute('data-color-option-position');

      // Find matching variant
      const variant = this._findVariant(colorValue, colorPosition, sizeValue, sizePosition);

      if (!variant || !variant.available) {
        return;
      }

      // Add to cart
      this.isLoading = true;
      button.classList.add('is-loading');

      try {
        await this._addToCart(variant.id);
        // Close overlay after successful add
        this._close();
      } finally {
        this.isLoading = false;
        button.classList.remove('is-loading');
      }
    }

    _findVariant(colorValue, colorPosition, sizeValue, sizePosition) {
      if (!this.productData) return null;

      // Find variant that matches color (if provided) and size
      return this.productData.variants.find(variant => {
        // Check size match
        if (variant[`option${sizePosition}`] !== sizeValue) {
          return false;
        }

        // Check color match (if color option exists)
        if (colorPosition && colorValue) {
          if (variant[`option${colorPosition}`] !== colorValue) {
            return false;
          }
        }

        return true;
      });
    }

    _updateButtonAvailability() {
      if (!this.productData) return;

      // Get selected color (if exists)
      const productItem = this.closest('product-item');
      const colorSwatch = productItem?.querySelector('.color-swatch__radio:checked');
      const colorValue = colorSwatch?.value;
      const colorSwatchList = colorSwatch?.closest('.product-item-meta__swatch-list');
      const colorPosition = colorSwatchList?.getAttribute('data-color-option-position');

      // Update each size button
      this.querySelectorAll('.product-item__size-button').forEach(button => {
        const sizeValue = button.getAttribute('data-option-value');
        const sizePosition = button.getAttribute('data-option-position');

        // Check if there's an available variant with this size
        const hasAvailableVariant = this.productData.variants.some(variant => {
          // Check size match
          if (variant[`option${sizePosition}`] !== sizeValue) {
            return false;
          }

          // Check color match (if color option exists)
          if (colorPosition && colorValue) {
            if (variant[`option${colorPosition}`] !== colorValue) {
              return false;
            }
          }

          return variant.available;
        });

        if (hasAvailableVariant) {
          button.removeAttribute('disabled');
        } else {
          button.setAttribute('disabled', '');
        }
      });
    }

    async _addToCart(variantId) {
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to add to cart: ${response.status}`);
        }

        const data = await response.json();

        // Dispatch variant:added event
        document.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: {
            variant: data.hasOwnProperty('items') ? data.items[0] : data
          }
        }));

        // Fetch updated cart
        const cartResponse = await fetch('/cart.js');
        const cartContent = await cartResponse.json();

        // Dispatch cart:updated event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));

        // Dispatch cart:refresh event with openMiniCart
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables?.settings?.cartType === 'drawer'
          }
        }));
      } catch (error) {
        console.error('Failed to add to cart:', error);
        alert('Sorry, there was an error adding this product to your cart. Please try again.');
      }
    }
  }

  window.customElements.define('quick-size-selector', QuickSizeSelector);

  // Listen for color swatch changes to update button availability
  document.addEventListener('change', async (event) => {
    const colorSwatch = event.target.closest('.color-swatch__radio');
    if (!colorSwatch) return;

    const productItem = colorSwatch.closest('product-item');
    const sizeSelector = productItem?.querySelector('quick-size-selector');

    if (sizeSelector) {
      // Load product data if not already loaded
      if (!sizeSelector.productData) {
        await sizeSelector._loadProductData();
      }

      // Update button availability with new color selection
      if (sizeSelector._updateButtonAvailability) {
        sizeSelector._updateButtonAvailability();
      }
    }
  });
}
